import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  ArrowRight,
  Shield,
  Scale,
  FileText,
  Users,
  CheckCircle,
  Building2,
  Briefcase,
  Gavel,
  FileCheck,
  AlertTriangle,
  Target,
  Clock,
  Leaf,
  Factory,
  ShoppingBag,
  Wheat,
  Droplets,
  Pill,
  Cpu
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { useLanguage } from '../contexts/LanguageContext';

const HolderLawyer = () => {
  const { t } = useLanguage();

  // Track record clients - logos from the slides
  const trackRecordClients = [
    { name: 'Natura', logo: 'https://upload.wikimedia.org/wikipedia/commons/thumb/8/8d/Natura_logo.svg/200px-Natura_logo.svg.png' },
    { name: 'Sodexo', logo: 'https://upload.wikimedia.org/wikipedia/commons/thumb/f/f9/Sodexo_logo.svg/200px-Sodexo_logo.svg.png' },
    { name: 'Sony', logo: 'https://upload.wikimedia.org/wikipedia/commons/thumb/c/ca/Sony_logo.svg/200px-Sony_logo.svg.png' },
    { name: 'Amway', logo: 'https://upload.wikimedia.org/wikipedia/commons/thumb/1/16/Amway_logo.svg/200px-Amway_logo.svg.png' },
    { name: 'Avon', logo: 'https://upload.wikimedia.org/wikipedia/commons/thumb/4/40/Avon_logo.svg/200px-Avon_logo.svg.png' },
    { name: 'Sanofi', logo: 'https://upload.wikimedia.org/wikipedia/commons/thumb/8/80/Sanofi_logo.svg/200px-Sanofi_logo.svg.png' },
    { name: 'Mary Kay', logo: 'https://upload.wikimedia.org/wikipedia/commons/thumb/3/33/Mary_Kay_logo.svg/200px-Mary_Kay_logo.svg.png' },
    { name: 'Lenovo', logo: 'https://upload.wikimedia.org/wikipedia/commons/thumb/b/b8/Lenovo_logo_2015.svg/200px-Lenovo_logo_2015.svg.png' },
    { name: 'Bunge', logo: 'https://upload.wikimedia.org/wikipedia/commons/thumb/5/5e/Bunge_logo.svg/200px-Bunge_logo.svg.png' },
    { name: 'Thales', logo: 'https://upload.wikimedia.org/wikipedia/commons/thumb/1/14/Thales_Logo.svg/200px-Thales_Logo.svg.png' },
    { name: 'Unisys', logo: 'https://upload.wikimedia.org/wikipedia/commons/thumb/0/0a/Unisys_logo_%282018%29.svg/200px-Unisys_logo_%282018%29.svg.png' },
  ];

  const competencies = [
    'lawyerPage.competence1',
    'lawyerPage.competence2',
    'lawyerPage.competence3',
    'lawyerPage.competence4',
    'lawyerPage.competence5',
    'lawyerPage.competence6',
  ];

  const services = [
    { icon: <Gavel className="h-6 w-6" />, titleKey: 'lawyerPage.service1', descKey: 'lawyerPage.service1Desc' },
    { icon: <FileText className="h-6 w-6" />, titleKey: 'lawyerPage.service2', descKey: 'lawyerPage.service2Desc' },
    { icon: <Building2 className="h-6 w-6" />, titleKey: 'lawyerPage.service3', descKey: 'lawyerPage.service3Desc' },
    { icon: <FileCheck className="h-6 w-6" />, titleKey: 'lawyerPage.service4', descKey: 'lawyerPage.service4Desc' },
    { icon: <Shield className="h-6 w-6" />, titleKey: 'lawyerPage.service5', descKey: 'lawyerPage.service5Desc' },
    { icon: <AlertTriangle className="h-6 w-6" />, titleKey: 'lawyerPage.service6', descKey: 'lawyerPage.service6Desc' },
  ];

  const areas = [
    { icon: <Scale className="h-6 w-6" />, titleKey: 'lawyerPage.areaTax', descKey: 'lawyerPage.areaTaxDesc' },
    { icon: <Building2 className="h-6 w-6" />, titleKey: 'lawyerPage.areaCorporate', descKey: 'lawyerPage.areaCorporateDesc' },
    { icon: <Users className="h-6 w-6" />, titleKey: 'lawyerPage.areaLabor', descKey: 'lawyerPage.areaLaborDesc' },
    { icon: <FileText className="h-6 w-6" />, titleKey: 'lawyerPage.areaCivil', descKey: 'lawyerPage.areaCivilDesc' },
    { icon: <Gavel className="h-6 w-6" />, titleKey: 'lawyerPage.areaCriminal', descKey: 'lawyerPage.areaCriminalDesc' },
    { icon: <Leaf className="h-6 w-6" />, titleKey: 'lawyerPage.areaEnvironmental', descKey: 'lawyerPage.areaEnvironmentalDesc' },
  ];

  const sectors = [
    { icon: <Factory className="h-8 w-8" />, titleKey: 'lawyerPage.sectorFood' },
    { icon: <ShoppingBag className="h-8 w-8" />, titleKey: 'lawyerPage.sectorDirectSales' },
    { icon: <Wheat className="h-8 w-8" />, titleKey: 'lawyerPage.sectorAgro' },
    { icon: <Droplets className="h-8 w-8" />, titleKey: 'lawyerPage.sectorOilGas' },
    { icon: <Pill className="h-8 w-8" />, titleKey: 'lawyerPage.sectorPharma' },
    { icon: <Cpu className="h-8 w-8" />, titleKey: 'lawyerPage.sectorTech' },
  ];

  const whyChoose = [
    'lawyerPage.whyChoose1',
    'lawyerPage.whyChoose2',
    'lawyerPage.whyChoose3',
    'lawyerPage.whyChoose4',
    'lawyerPage.whyChoose5',
    'lawyerPage.whyChoose6',
  ];

  const approach = [
    { icon: <Shield className="h-6 w-6" />, titleKey: 'lawyerPage.prevention', descKey: 'lawyerPage.preventionDesc' },
    { icon: <Target className="h-6 w-6" />, titleKey: 'lawyerPage.strategy', descKey: 'lawyerPage.strategyDesc' },
    { icon: <CheckCircle className="h-6 w-6" />, titleKey: 'lawyerPage.results', descKey: 'lawyerPage.resultsDesc' },
  ];

  return (
    <div className="min-h-screen pt-20" data-testid="holder-lawyer-page">
      {/* Hero Section */}
      <section className="py-24 md:py-32 bg-stone-50 noise-bg">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
            >
              <span className="text-bronze font-medium text-sm tracking-wider uppercase mb-4 block">
                {t('lawyerPage.heroTag')}
              </span>
              <h1 className="text-4xl md:text-5xl font-serif font-bold text-stone-900 mb-6">
                {t('lawyerPage.heroTitle')}{' '}
                <span className="gold-sheen">{t('lawyerPage.heroHighlight')}</span>
              </h1>
              <p className="text-lg text-stone-600 mb-4 leading-relaxed">
                {t('lawyerPage.heroDesc')}
              </p>
              <p className="text-base text-emerald font-medium mb-8">
                {t('lawyerPage.heroSubtitle')}
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button 
                  asChild
                  className="bg-emerald hover:bg-emerald-light text-white rounded-sm px-8 py-6 text-lg font-serif btn-shine"
                  data-testid="lawyer-cta-primary"
                >
                  <Link to="/contato" className="flex items-center gap-2">
                    {t('lawyerPage.ctaPrimary')}
                    <ArrowRight className="h-5 w-5" />
                  </Link>
                </Button>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              className="hidden lg:block"
            >
              <div className="relative">
                <div className="absolute -inset-4 bg-emerald/10 rounded-sm blur-2xl" />
                <img 
                  src="https://images.unsplash.com/photo-1589829545856-d10d557cf95f?w=800&q=80"
                  alt="Legal advisory"
                  className="relative rounded-sm shadow-2xl w-full"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Partnership & Competencies Section */}
      <section className="py-24 md:py-32 bg-white" data-testid="lawyer-partnership">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <span className="text-bronze font-medium text-sm tracking-wider uppercase mb-4 block">
              {t('lawyerPage.partnershipTag')}
            </span>
            <h2 className="text-3xl md:text-4xl font-serif font-bold text-stone-900 mb-4">
              {t('lawyerPage.partnershipTitle')}
            </h2>
            <p className="text-emerald font-medium text-lg max-w-2xl mx-auto">
              {t('lawyerPage.partnershipDesc')}
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {competencies.map((itemKey, index) => (
              <motion.div
                key={itemKey}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-stone-50 p-6 rounded-sm border border-stone-200"
                data-testid={`lawyer-competency-${index}`}
              >
                <div className="flex items-start gap-4">
                  <CheckCircle className="h-6 w-6 text-emerald flex-shrink-0 mt-1" />
                  <p className="text-stone-700">{t(itemKey)}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-16 bg-emerald">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {[
              { number: '20+', labelKey: 'lawyerPage.statsExperience' },
              { number: '500+', labelKey: 'lawyerPage.statsCases' },
              { number: '95%', labelKey: 'lawyerPage.statsSuccess' },
              { number: '24h', labelKey: 'lawyerPage.statsResponse' },
            ].map((stat, index) => (
              <motion.div
                key={stat.labelKey}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="text-center"
              >
                <p className="text-3xl md:text-4xl font-serif font-bold text-white mb-2">
                  {stat.number}
                </p>
                <p className="text-stone-300 text-sm">{t(stat.labelKey)}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Areas of Expertise - Expanded */}
      <section className="py-24 md:py-32 bg-stone-50 noise-bg" data-testid="lawyer-areas">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <span className="text-bronze font-medium text-sm tracking-wider uppercase mb-4 block">
              {t('lawyerPage.areasTag')}
            </span>
            <h2 className="text-3xl md:text-4xl font-serif font-bold text-stone-900 mb-4">
              {t('lawyerPage.areasTitle')}
            </h2>
            <p className="text-stone-600 max-w-2xl mx-auto">
              {t('lawyerPage.areasDesc')}
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {areas.map((area, index) => (
              <motion.div
                key={area.titleKey}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-white p-6 rounded-sm border border-stone-200 text-center"
                data-testid={`lawyer-area-${index}`}
              >
                <div className="w-14 h-14 rounded-sm bg-emerald/10 flex items-center justify-center text-emerald mx-auto mb-4">
                  {area.icon}
                </div>
                <h3 className="text-lg font-serif font-semibold text-stone-900 mb-1">
                  {t(area.titleKey)}
                </h3>
                <p className="text-stone-600 text-sm">
                  {t(area.descKey)}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Sectors */}
      <section className="py-24 md:py-32 bg-white" data-testid="lawyer-sectors">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <span className="text-bronze font-medium text-sm tracking-wider uppercase mb-4 block">
              {t('lawyerPage.sectorsTag')}
            </span>
            <h2 className="text-3xl md:text-4xl font-serif font-bold text-stone-900 mb-4">
              {t('lawyerPage.sectorsTitle')}
            </h2>
            <p className="text-stone-600 max-w-2xl mx-auto">
              {t('lawyerPage.sectorsDesc')}
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6">
            {sectors.map((sector, index) => (
              <motion.div
                key={sector.titleKey}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: index * 0.05 }}
                className="bg-stone-50 p-6 rounded-sm border border-stone-200 text-center hover:shadow-md hover:-translate-y-1 transition-all duration-300"
                data-testid={`lawyer-sector-${index}`}
              >
                <div className="w-16 h-16 rounded-full bg-emerald/10 flex items-center justify-center text-emerald mx-auto mb-4">
                  {sector.icon}
                </div>
                <h3 className="text-sm font-serif font-semibold text-stone-900">
                  {t(sector.titleKey)}
                </h3>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Track Record - Client Logos */}
      <section className="py-24 md:py-32 bg-stone-50 noise-bg" data-testid="lawyer-track-record">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <span className="text-bronze font-medium text-sm tracking-wider uppercase mb-4 block">
              {t('lawyerPage.trackRecordTag')}
            </span>
            <h2 className="text-3xl md:text-4xl font-serif font-bold text-stone-900 mb-4">
              {t('lawyerPage.trackRecordTitle')}
            </h2>
            <p className="text-stone-600 max-w-2xl mx-auto">
              {t('lawyerPage.trackRecordDesc')}
            </p>
          </div>

          <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-8 items-center justify-items-center">
            {trackRecordClients.map((client, index) => (
              <motion.div
                key={client.name}
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.05 }}
                className="bg-white p-4 rounded-sm shadow-sm hover:shadow-md transition-shadow w-full flex items-center justify-center h-24"
                data-testid={`lawyer-client-${index}`}
              >
                <img 
                  src={client.logo} 
                  alt={client.name}
                  className="max-h-12 max-w-full object-contain filter grayscale hover:grayscale-0 transition-all duration-300"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.parentElement.innerHTML = `<span class="text-stone-600 font-medium text-sm">${client.name}</span>`;
                  }}
                />
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="py-24 md:py-32 bg-white" data-testid="lawyer-services">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <span className="text-bronze font-medium text-sm tracking-wider uppercase mb-4 block">
              {t('lawyerPage.servicesTag')}
            </span>
            <h2 className="text-3xl md:text-4xl font-serif font-bold text-stone-900 mb-4">
              {t('lawyerPage.servicesTitle')}
            </h2>
            <p className="text-stone-600 max-w-2xl mx-auto">
              {t('lawyerPage.servicesDesc')}
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((service, index) => (
              <motion.div
                key={service.titleKey}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-stone-50 p-6 rounded-sm border border-stone-200 hover:shadow-md hover:-translate-y-1 transition-all duration-300"
                data-testid={`lawyer-service-${index}`}
              >
                <div className="w-12 h-12 rounded-sm bg-emerald/10 flex items-center justify-center text-emerald mb-4">
                  {service.icon}
                </div>
                <h3 className="text-lg font-serif font-semibold text-stone-900 mb-2">
                  {t(service.titleKey)}
                </h3>
                <p className="text-stone-600 text-sm">
                  {t(service.descKey)}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-24 md:py-32 bg-emerald text-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl md:text-4xl font-serif font-bold mb-8">
                {t('lawyerPage.whyChooseTitle')}
              </h2>
              <div className="grid grid-cols-1 gap-4">
                {whyChoose.map((itemKey) => (
                  <div 
                    key={itemKey}
                    className="flex items-center gap-4 bg-white/10 p-4 rounded-sm"
                  >
                    <CheckCircle className="h-5 w-5 text-bronze flex-shrink-0" />
                    <span className="text-stone-200">{t(itemKey)}</span>
                  </div>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <div className="text-center mb-8">
                <span className="text-bronze font-medium text-sm tracking-wider uppercase mb-4 block">
                  {t('lawyerPage.approachTag')}
                </span>
                <h3 className="text-2xl font-serif font-bold">
                  {t('lawyerPage.approachTitle')}
                </h3>
              </div>
              <div className="space-y-6">
                {approach.map((item) => (
                  <div 
                    key={item.titleKey}
                    className="bg-white/10 backdrop-blur-sm p-6 rounded-sm"
                  >
                    <div className="flex items-center gap-4 mb-3">
                      <div className="w-10 h-10 rounded-sm bg-bronze/20 flex items-center justify-center text-bronze">
                        {item.icon}
                      </div>
                      <h4 className="font-serif font-semibold">{t(item.titleKey)}</h4>
                    </div>
                    <p className="text-stone-300 text-sm pl-14">
                      {t(item.descKey)}
                    </p>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 md:py-32 bg-stone-50 noise-bg">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <Scale className="h-16 w-16 text-bronze mx-auto mb-6" />
            <h2 className="text-3xl md:text-4xl font-serif font-bold text-stone-900 mb-6">
              {t('lawyerPage.ctaTitle')}
            </h2>
            <p className="text-stone-600 text-lg mb-8">
              {t('lawyerPage.ctaDesc')}
            </p>
            <Button 
              asChild
              className="bg-emerald hover:bg-emerald-light text-white rounded-sm px-8 py-6 text-lg font-serif btn-shine"
              data-testid="lawyer-final-cta"
            >
              <Link to="/contato" className="flex items-center gap-2">
                {t('lawyerPage.ctaBtn')}
                <ArrowRight className="h-5 w-5" />
              </Link>
            </Button>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default HolderLawyer;
